import {
    ISoundPlayer,
    DefaultEventsMap,
    IEventManagerWithAsync,
    DeviceType,
    IView,
    ISettings, SystemComponentType, ComponentModuleViewConfig, ScreenOrientation
} from '@gongxigames/framework/core/types';
import {CoreDI, CoreSettingsParameters} from '@gongxigames/framework/core/configs';
import {CoreEventsMap} from '@gongxigames/framework/core/events';
import {inject, injectable} from '@gongxigames/framework/core/installer';
import {EventManager} from '@gongxigames/framework/core/fragments/EventManager';
import {consoleLogger} from '@gongxigames/framework/core/fragments/ConsoleLogger';
import {ScreenParameters} from '@gongxigames/framework/core/fragments/ScreenParameters';
import {Device} from '@gongxigames/framework/core/services/Device';
import {ScreenManager} from '@gongxigames/framework/core/services/screenManager/ScreenManager';
import {System} from '@gongxigames/framework/core/services/system/System';
import {ResolutionManager} from '@gongxigames/framework/core/services/ResolutionManager';
import {UpdateManager} from '@gongxigames/framework/core/services/UpdateManager';

@injectable()
export abstract class BaseView<EventsMap = DefaultEventsMap> extends EventManager<EventsMap> implements IView<EventsMap> {
    public type: SystemComponentType = 'view';

    @inject(CoreDI.IEventManagerWithAsync)
    public readonly eventManager!: IEventManagerWithAsync<EventsMap>;

    @inject(CoreDI.Device)
    protected _device!: Device;

    @inject(CoreDI.SoundPlayer)
    protected _soundPlayer!: ISoundPlayer;

    @inject(CoreDI.ScreenManager)
    protected _screenManager!: ScreenManager;

    @inject(CoreDI.System)
    protected _system!: System;

    @inject(CoreDI.ResolutionManager)
    protected _resolutionManager!: ResolutionManager;

    @inject(CoreDI.Settings)
    protected _settings!: ISettings<CoreSettingsParameters>;

    @inject(CoreDI.UpdateManager)
    protected _updateManager!: UpdateManager;

    public forceUpdateOrientation(): void {
        if (this.isPlatform(DeviceType.MOBILE)) {
            if (this._screenManager.orientation === ScreenOrientation.LANDSCAPE) {
                this.onLandscape();
            } else if (this._screenManager.orientation === ScreenOrientation.PORTRAIT) {
                this.onPortrait();
            }
            this.onOrientationChanged();
        }
    }
    public get soundPlayer(): ISoundPlayer {
        return this._soundPlayer;
    }

    public isPlatform(platform: string): boolean {
        return this._settings.get('platform') === platform;
    }

    public isPlatformDesktop(): boolean {
        return this.isPlatform(DeviceType.DESKTOP);
    }

    public isPlatformMobile(): boolean {
        return this.isPlatform(DeviceType.MOBILE);
    }

    public get updateManager(): UpdateManager {
        return this._updateManager;
    }

    public configure(componentConfig: ComponentModuleViewConfig): void {
        this.setInitParameters(componentConfig);
    }

    public init(): void {
        this.setupEvents();
    }

    protected setInitParameters(parameters: ComponentModuleViewConfig): void {}

    public destroy(): void {
        consoleLogger.log(`View ${this.constructor.name} destroyed`);
    }

    public onResourcesLoaded(): void {}

    public onLandscape(): void {}

    public onPortrait(): void {}

    public onOrientationChanged(): void {}

    public onResized(screenParams: ScreenParameters): void {}

    public getViewDependencies(): (symbol|string)[] {
        return [];
    }

    public getSettings<Settings extends ISettings<any>>(): Settings {
        return this._settings as Settings;
    }

    protected setupEvents(): void {
        const eventManager = (this.eventManager as IEventManagerWithAsync<CoreEventsMap>);

        if (!this.isPlatform(DeviceType.DESKTOP)) {
            eventManager.on('General.Screen.LANDSCAPE', this.onLandscape.bind(this), this);
            eventManager.on('General.Screen.PORTRAIT', this.onPortrait.bind(this), this);
            eventManager.on('General.Screen.ORIENTATION_CHANGED', this.onOrientationChanged.bind(this), this);
        }

        eventManager.on('General.Screen.RESIZED', this.onResized.bind(this), this);
    }

    protected removeEvents(): void {
        const eventManager = (this.eventManager as IEventManagerWithAsync<CoreEventsMap>);

        if (!this.isPlatform(DeviceType.DESKTOP)) {
            eventManager.off('General.Screen.LANDSCAPE', this);
            eventManager.off('General.Screen.PORTRAIT', this);
            eventManager.off('General.Screen.ORIENTATION_CHANGED', this);
        }

        eventManager.off('General.Screen.RESIZED', this);
    }
}
